import React from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import colorsVars from 'styles/colors.module.scss';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { cardsDataQueryKey } from 'api/queries/cardsData.query';

import { ActivityCell } from 'components/common/Tables/Cells/ActivityCell/ActivityCell';
import { ActivityIdCell } from 'components/common/Tables/Cells/ActivityIdCell/ActivityIdCell';
import { AssigneeCell } from 'components/common/Tables/Cells/AssigneeCell/AssigneeCell';
import { DetailsCell } from 'components/common/Tables/Cells/DetailsCell/DetailsCell';
import { DynamicChipsCell } from 'components/common/Tables/Cells/DynamicChipsCell/DynamicChipsCell';
import { IndicatorsCell } from 'components/common/Tables/Cells/IndicatorsCell/IndicatorsCell';
import { StatusCell } from 'components/common/Tables/Cells/StatusCell/StatusCell';
import { TooltipCellWithCopy } from 'components/common/Tables/Cells/TooltipCellWithCopy/TooltipCellWithCopy';
import { VarianceCell } from 'components/common/Tables/Cells/VarianceCell/VarianceCell';
import { SubmittedDurationCompleteCell } from 'components/common/Tables/Cells/submittedDurationCompleteCell/SubmittedDurationCompleteCell';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';
import { TrackedUntrackedActionCellWrapper } from 'components/common/TrackedUntrackedAction/trackedUntrackedActionCellWrapper/trackedUntrackedActionCellWrapper';

import { getStatusLabelByOriginalStatus } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import CriticalityScoreCell from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/Cells/criticalityScoreCell';
import { sortFloat } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/utils';

const { color6: orange, textColor, delayDriverColor } = colorsVars;

const invalidateKeys = [cardsDataQueryKey, 'summaryTrackedActivitiesTableData'];

export const initialState: ITableColumnState = {
    hiddenColumns: [
        'startDate',
        'finishDate',
        'currentStartDate',
        'currentFinishDate',
        'lateFinish',
        'lateStart',
        'baselineStartDate',
        'baselineFinishDate',
        'plannedStartDate',
        'plannedFinishDate',
        'actualStartDate',
        'actualFinishDate',
        'plannedDuration',
        'actualDuration',
        'remainingDuration',
        'completedDuration',
        'durationOverrun',
        'numOfPredecessors',
        'numOfSuccessors',
        'projectWeekStart',
        'projectWeekFinish',
        'projectMonthStart',
        'projectMonthFinish',
        'parentName',
        'grandParentName',
        'tagList',
        'criticalPath',
        'originalStatus',
    ],
};
export const columns = [
    {
        Header: 'Activity Name',
        accessor: 'title',
        ...columnsCommonConfig.activity,
        Cell: ({ row, toggleAllRowsSelected }) => {
            const { taskVersionHashCode, isTracked, isVirtual } = row.original;
            const activityName = row.original.title;
            const taskId = typeof row.original.id === 'string' ? row.original.id.split('_')[0] : row.original.id;
            const { toggleRowSelected } = row;

            return (
                <ActivityCell
                    activityName={activityName}
                    taskId={taskId}
                    isVirtual={isVirtual}
                    taskVersionHashCode={taskVersionHashCode}
                    isTracked={isTracked}
                    toggleRowSelected={toggleRowSelected}
                    toggleAllRowsSelected={toggleAllRowsSelected}
                    trackedUntrackedActionCell={
                        <TrackedUntrackedActionCellWrapper
                            isTracked={isTracked}
                            taskVersionHashCode={taskVersionHashCode}
                            invalidateQueriesKey={invalidateKeys}
                        />
                    }
                />
            );
        },
    },
    {
        Header: 'Activity ID',
        accessor: (row) => {
            return row.type.id === 2
                ? row.taskActivityId
                : `C-${typeof row.id === 'string' ? row.id.split('_')[0] : row.id}`;
        },
        ...columnsCommonConfig.activityId,
        Cell: ({ row }) => {
            const activityId =
                row.original.type.id === 2
                    ? row.original.taskActivityId
                    : `C-${typeof row.original.id === 'string' ? row.original.id.split('_')[0] : row.original.id}`;

            return <ActivityIdCell activityId={activityId} />;
        },
    },
    {
        Header: 'Risk Indicators',
        accessor: 'title',
        ...columnsCommonConfig.riskIndicators,
        Cell: ({ row }) => {
            const cardIndicators = row.original.cardIndicators;

            return <IndicatorsCell cardIndicators={cardIndicators} />;
        },
    },
    {
        Header: 'Status',
        accessor: 'status.label',
        ...columnsCommonConfig.status,
        Cell: ({ row }) => {
            const { status, isVirtual, originalStatus, id, isInterimChanged } = row.original;
            const cardType = row.original.type.name;
            const category = row.original.category;

            return (
                <StatusCell
                    status={status}
                    isVirtual={isVirtual}
                    taskId={id}
                    originalStatus={originalStatus}
                    invalidateQueriesKey={invalidateKeys}
                    cardType={cardType}
                    category={category}
                    isInterimChanged={isInterimChanged}
                />
            );
        },
    },
    {
        Header: 'Assignee',
        accessor: 'assignee.firstName',
        ...columnsCommonConfig.assignee,
        Cell: ({ row }) => {
            const { assignee, isVirtual, id } = row.original;

            return (
                <AssigneeCell
                    assignee={assignee}
                    taskId={id}
                    invalidateQueriesKey={invalidateKeys}
                    isVirtual={isVirtual}
                />
            );
        },
    },
    {
        Header: '% Complete',
        ...columnsCommonConfig.submittedDurationComplete,
        accessor: 'submittedDurationComplete',
        Cell: ({ value }) => {
            return <SubmittedDurationCompleteCell value={value} />;
        },
    },
    {
        Header: 'Activity Type',
        ...columnsCommonConfig.activityType,
        accessor: 'taskActivityType',
        Cell: ({ value }) => <TooltipCellWithCopy text={value} />,
    },
    {
        Header: 'Details/Last Comment',
        accessor: 'details',
        ...columnsCommonConfig.details,
        Cell: ({ row }) => {
            const { cardLastComment, details, id, isVirtual, isGenAiDetails } = row.original;
            return (
                <DetailsCell
                    details={details}
                    cardLastComment={cardLastComment}
                    id={id}
                    invalidateQueriesKey={invalidateKeys}
                    isVirtual={isVirtual}
                    isGenAiDetails={isGenAiDetails}
                />
            );
        },
    },
    {
        Header: 'Tags',
        accessor: 'tagList',
        ...columnsCommonConfig.tagList,
        Cell: ({ value }) => {
            const list = value.map((item) => item.name);
            return list.length > 0 ? <DynamicChipsCell list={list} type={'tag'} /> : null;
        },
    },
    {
        Header: 'Criticality Score',
        accessor: 'criticalityScore',
        ...columnsCommonConfig.criticalityScore,
        Cell: ({ value, row }) => {
            const delayDriver = row.original.cardIndicators.isDelayDriver.isIndicatorOn;
            const style = delayDriver ? { color: delayDriverColor, fontWeight: 600 } : {};
            const numOfPredecessors = row.original.numOfPredecessors || row.original.predeccessors?.length;
            const numOfSuccessors = row.original.numOfSuccessors || row.original.successors?.length;
            const precedingActivities = row.original.precedingActivities;
            const succeedingActivites = row.original.succeedingActivites;
            const precedingRisk = row.original.precedingRisk;
            const succeedingRisk = row.original.succeedingRisk;
            return (
                <CriticalityScoreCell
                    succeedingRisk={succeedingRisk}
                    precedingRisk={precedingRisk}
                    numOfSuccessors={numOfSuccessors}
                    numOfPredecessors={numOfPredecessors}
                    succeedingActivites={succeedingActivites}
                    precedingActivities={precedingActivities}
                    style={style}
                    value={value}
                />
            );
        },
        sortType: 'basic',
    },
    {
        Header: 'Float',
        accessor: 'float',
        ...columnsCommonConfig.float,
        sortType: sortFloat,
    },
    {
        Header: 'Critical Path',
        accessor: (row) => (row.cardIndicators.isCriticalPath.isIndicatorOn ? 'Yes' : 'No'),
        ...columnsCommonConfig.criticalPath,
    },
    {
        Header: 'Start Date',
        ...columnsCommonConfig.startDate,
        accessor: 'startDate',
        Cell: ({ value }) => {
            return value ? moment(value).format(constants.formats.date.default) : value;
        },
    },
    {
        Header: 'Finish Date',
        ...columnsCommonConfig.finishDate,
        accessor: 'endDate',
        Cell: ({ value }) => {
            return value ? moment(value).format(constants.formats.date.default) : value;
        },
    },
    {
        Header: 'Finish Variance',
        ...columnsCommonConfig.lateFinish,
        accessor: 'finishVariance',
        Cell: ({ value }) => {
            return <VarianceCell value={value} />;
        },
    },
    {
        Header: 'Start Variance',
        ...columnsCommonConfig.lateStart,
        accessor: 'startVariance',
        Cell: ({ value }) => {
            return <VarianceCell value={value} />;
        },
    },
    {
        Header: 'Baseline Start Date ',
        accessor: 'baselineStartDate',
        ...columnsCommonConfig.baselineStartDate,
        Cell: ({ value }) => {
            return value ? moment(value).format(constants.formats.date.default) : value;
        },
    },
    {
        Header: 'Baseline Finish Date',
        accessor: 'baselineFinishDate',
        ...columnsCommonConfig.baselineFinishDate,
        Cell: ({ value }) => {
            return value ? moment(value).format(constants.formats.date.default) : value;
        },
    },
    {
        Header: 'Planned Start Date ',
        accessor: 'plannedStartDate',
        ...columnsCommonConfig.plannedStartDate,
        Cell: ({ value }) => {
            return value ? moment(value).format(constants.formats.date.default) : value;
        },
    },
    {
        Header: 'Planned Finish Date',
        accessor: 'plannedFinishDate',
        ...columnsCommonConfig.plannedFinishDate,
        Cell: ({ value }) => {
            return value ? moment(value).format(constants.formats.date.default) : value;
        },
    },
    {
        Header: 'Actual Start Date',
        accessor: 'actualStartDate',
        ...columnsCommonConfig.actualStartDate,
        Cell: ({ value }) => {
            return value ? moment(value).format(constants.formats.date.default) : value;
        },
    },
    {
        Header: 'Actual Finish Date',
        accessor: 'actualFinishDate',
        ...columnsCommonConfig.actualFinishDate,
        Cell: ({ value }) => {
            return value ? moment(value).format(constants.formats.date.default) : value;
        },
    },
    {
        Header: 'Planned<br /> Duration',
        ...columnsCommonConfig.plannedDuration,
        accessor: 'plannedDurationInDays',
    },
    {
        Header: 'Actual<br /> Duration',
        ...columnsCommonConfig.actualDuration,
        accessor: 'actualDurationInDays',
    },
    {
        Header: 'Remaining<br /> Duration',
        ...columnsCommonConfig.remainingDuration,
        accessor: 'remainingDurationInDays',
    },
    {
        Header: 'Current/<br />Completed<br /> Duration',
        ...columnsCommonConfig.completedDuration,
        accessor: 'completedDurationInDays',
    },
    {
        Header: 'Current Duration<br /> Overrun',
        ...columnsCommonConfig.durationOverrun,
        accessor: 'completedDurationOverrun',
        Cell: ({ value }) => {
            const color = value > 0 ? orange : textColor;
            return <span style={{ color }}>{value}</span>;
        },
    },
    {
        Header: 'Pred.',
        accessor: 'numOfPredecessors',
        ...columnsCommonConfig.numOfPredecessors,
    },
    {
        Header: 'Succ.',
        accessor: 'numOfSuccessors',
        ...columnsCommonConfig.numOfSuccessors,
    },
    {
        Header: 'Project Week Start ',
        accessor: 'projectWeekStart',
        ...columnsCommonConfig.projectWeekStart,
    },
    {
        Header: 'Project Week Finish',
        accessor: 'projectWeekFinish',
        ...columnsCommonConfig.projectWeekFinish,
    },
    {
        Header: 'Project Month Start',
        accessor: 'projectMonthStart',
        ...columnsCommonConfig.projectMonthStart,
    },
    {
        Header: 'Project Month Finish',
        accessor: 'projectMonthFinish',
        ...columnsCommonConfig.projectMonthFinish,
    },
    {
        Header: 'Immediate Parent Name ',
        ...columnsCommonConfig.parentName,
        accessor: 'parentActivity',
        Cell: ({ value }) => <TooltipCellWithCopy text={value} />,
    },
    {
        Header: 'Grand Parent Name',
        ...columnsCommonConfig.grandParentName,
        accessor: 'grandParentActivity',
        Cell: ({ value }) => <TooltipCellWithCopy text={value} />,
    },
    {
        Header: 'Last Submitted Status',
        ...columnsCommonConfig.originalStatus,
        accessor: 'originalStatus',
        Cell: ({ value }) => getStatusLabelByOriginalStatus(value),
    },
];

export const initialFiltersState = {
    filter: {
        filters: [],
    },
    paging: {
        page: 0,
        size: 40,
    },
    withDetails: true,
};

export const statusOptions: ISelectOption<string>[] = [
    {
        value: 'TODO',
        label: 'To Do',
    },
    {
        value: 'IN_PROGRESS',
        label: 'In Progress',
    },
    {
        value: 'DONE',
        label: 'Done',
    },
];

export const moreOptionsListItems: ISelectOption<string>[] = [
    { value: 'LateFinishDate', label: 'Late Finish Date' },
    { value: 'LateStartDate', label: 'Late Start Date' },
    { value: 'CriticalPath', label: 'Critical Path' },
    { value: 'Milestones', label: 'Milestones' },
];

export const NEXT_DATE_FILTERS: ISelectOption<string>[] = [
    {
        label: '7D',
        value: '7',
    },
    {
        label: '14D',
        value: '14',
    },
    {
        label: '30D',
        value: '30',
    },
    {
        label: 'ALL',
        value: '-1',
    },
];

export const getRiskIndicatorsQueryStringValue = (value) => {
    switch (value) {
        case 'LateFinishDate':
            return 'FINISH_PROG_OVERDUE_CARD_BOARD';
        case 'LateStartDate':
            return 'START_PROG_OVERDUE_CARD_BOARD';
        case 'CriticalPath':
            return 'CRITICAL_PATH_USER_CARD_BOARD';
        default:
            return '';
    }
};

export const getEndDatesRangeQueryStringValue = (value) => {
    switch (value) {
        case '7':
            return 'TODAY_REF_NEXT_7_DAYS';
        case '14':
            return 'TODAY_REF_NEXT_14_DAYS';
        case '30':
            return 'TODAY_REF_NEXT_30_DAYS';
        case '-1':
            return null;
        default:
            return null;
    }
};
